// Frontend: LeavePolicyManagement.jsx - COMPLETE CODE (UPDATED: Improved carry forward handling with type/limit, better extraction parsing, UI adjustments)
import React, { useState, useEffect } from 'react';
import {
  Box, Container, Paper, Typography, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, CircularProgress, Grid, Alert, IconButton,
  Snackbar, Stepper, Step, StepLabel,
  Divider, FormControl, InputLabel, Select, MenuItem, Card, CardContent,
  Stack, Fade, Skeleton, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getCycleDisplay = (startMonth) => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const start = monthNames[startMonth !== undefined ? startMonth : 0];
  const end = monthNames[(startMonth - 1 + 12) % 12];
  return `${start} to ${end}`;
};

const LeavePolicyManagement = () => {
  // State Management
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [inputMode, setInputMode] = useState('manual');

  const initialTypeData = {
    days: '',
    carry_forward_type: 'none',
    carry_forward_limit: '',
    paid_status: 'paid'
  };

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    effective_date: '',
    created_by: '',
    annual: { ...initialTypeData },
    sick: { ...initialTypeData },
    casual: { ...initialTypeData },
    maternity: { ...initialTypeData },
    paternity: { ...initialTypeData },
    bereavement: { ...initialTypeData },
    general_rules: '',
    cycle_start_month: 0
  });

  const [customLeaveTypes, setCustomLeaveTypes] = useState([]);
  const [newLeaveTypeName, setNewLeaveTypeName] = useState('');

  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  const steps = ['Input Method', 'Policy Details'];
  const leaveTypeIcons = {
    annual: '🏖️',
    sick: '🏥',
    casual: '📅',
    maternity: '🤱',
    paternity: '🧑‍🍼',
    bereavement: '🕊️'
  };
  const baseLeaveTypes = ['annual', 'sick', 'casual', 'maternity', 'paternity', 'bereavement'];
  const allLeaveTypes = [...baseLeaveTypes, ...customLeaveTypes];

  // Effects
  useEffect(() => {
    fetchCompanyId();
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchPolicy();
    }
  }, [companyId]);

  // API Functions
  const fetchCompanyId = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const cid = payload.company_id || payload.company_registration_no;
        setCompanyId(cid);
        const createdBy = payload.name || payload.email || 'HR Admin';
        setFormData(prev => ({ ...prev, created_by: createdBy }));

        try {
          const cmpRes = await axios.get(`${API_URL}/api/cmp/${cid}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (cmpRes.data && cmpRes.data.data) {
            setCompanyConfig({
              dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
              timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
            });
          }
        } catch(e) {}
      } else {
        setError('Authentication token not found. Please log in again.');
      }
    } catch (err) {
      //console.error('Error fetching company ID:', err);
      setError('Failed to authenticate. Please log in again.');
    }
  };

  const fetchPolicy = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/policies`,   {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      const policies = res.data.policies || [];
      if (policies.length > 0) {
        setPolicy(policies[0]);
      } else {
        setPolicy(null);
      }
    } catch (err) {
      //console.error('Error fetching policy:', err);
      const errorMsg = err.response?.data?.error || 'Failed to fetch policy';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Form Handlers
  const loadPolicyToForm = (pol) => {
    const updatedFormData = {
      title: pol.title || '',
      content: pol.content || '',
      effective_date: pol.effective_date ? pol.effective_date.split('T')[0] : '',
      created_by: pol.created_by || formData.created_by,
      general_rules: pol.general_rules || '',
      cycle_start_month: pol.cycle_start_month !== undefined ? pol.cycle_start_month : 0
    };

    let parsedCustomPolicies = {};
    if (pol.custom_policies) {
      try {
        parsedCustomPolicies = typeof pol.custom_policies === 'string'
          ? JSON.parse(pol.custom_policies)
          : pol.custom_policies;
      } catch (e) {
        //console.error('Error parsing custom policies:', e);
      }
    }

    const loadedCustomTypes = Object.keys(parsedCustomPolicies);
    setCustomLeaveTypes(loadedCustomTypes);

    const typesToLoad = [...baseLeaveTypes, ...loadedCustomTypes];

    typesToLoad.forEach(type => {
      let parsedData = { ...initialTypeData };
      let sourceData = baseLeaveTypes.includes(type) ? pol[type] : parsedCustomPolicies[type];

      if (sourceData) {
        try {
          parsedData = typeof sourceData === 'string' ? JSON.parse(sourceData) : sourceData;
        } catch (e) {
          //console.error(`Error parsing ${type} leave:`, e);
        }
      }
      const cf = parsedData.carry_forward || '';
      delete parsedData.carry_forward;
      let cfType = 'none';
      let cfLimit = '';
      if (cf.toLowerCase().includes('all') || cf.toLowerCase().includes('unlimited')) {
        cfType = 'all';
      } else if (cf.match(/max\s*(\d+)/i) || cf.match(/up to\s*(\d+)/i)) {
        const numMatch = cf.match(/(\d+)/);
        if (numMatch) {
          cfLimit = numMatch[1];
          cfType = 'limited';
        }
      }
      parsedData.carry_forward_type = cfType;
      parsedData.carry_forward_limit = cfLimit;
      updatedFormData[type] = parsedData;
    });

    setFormData(prev => ({ ...prev, ...updatedFormData }));
    setIsEditMode(true);
  };

  const handleAddCustomLeaveType = () => {
    if (!newLeaveTypeName.trim()) return;
    const formattedName = newLeaveTypeName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (allLeaveTypes.includes(formattedName)) {
      showSnackbar('Leave type already exists', 'error');
      return;
    }
    setCustomLeaveTypes(prev => [...prev, formattedName]);
    setFormData(prev => ({
      ...prev,
      [formattedName]: { ...initialTypeData, displayName: newLeaveTypeName.trim() }
    }));
    setNewLeaveTypeName('');
    showSnackbar(`Added ${newLeaveTypeName} successfully`, 'success');
  };

  const handleTypeFieldChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleGeneralChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = ['application/pdf', 'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(selectedFile.type)) {
        showSnackbar('Please upload a PDF or Word document', 'error');
        e.target.value = '';
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        showSnackbar('File size must be less than 10MB', 'error');
        e.target.value = '';
        return;
      }

      setFile(selectedFile);
    }
  };

const handleDownloadTemplate = () => {
    try {
      const htmlContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' 
              xmlns:w='urn:schemas-microsoft-com:office:word' 
              xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset="utf-8">
          <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
            /* Word-specific styles */
            body { font-family: 'Calibri', 'Arial', sans-serif; font-size: 12pt; line-height: 1.6; padding: 40px; }
            h1 { font-size: 24pt; color: #2c3e50; text-align: center; border-bottom: 3px solid #3498db; padding-bottom: 15px; margin-bottom: 30px; }
            h2 { font-size: 18pt; color: #2980b9; margin-top: 25px; margin-bottom: 15px; background-color: #ecf0f1; padding: 8px 15px; }
            .info { text-align: center; font-size: 14pt; margin-bottom: 30px; color: #7f8c8d; }
            .leave-type { background-color: #f8f9fa; border-left: 4px solid #3498db; padding: 12px 18px; margin-bottom: 12px; }
            .leave-type strong { display: inline-block; min-width: 150px; color: #2c3e50; }
            .rules { background-color: #fef9e7; border: 1px solid #f39c12; padding: 15px 20px; margin-top: 20px; }
            .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #bdc3c7; color: #7f8c8d; font-size: 10pt; }
            .field { color: #2c3e50; font-weight: normal; }
            ul { margin-top: 5px; margin-bottom: 10px; }
            li { margin-bottom: 4px; }
          </style>
        </head>
        <body>
          <h1>LEAVE MANAGEMENT POLICY TEMPLATE</h1>
          
          <div class="info">
            <strong>Company:</strong> [Your Company Name]<br>
            <strong>Effective Date:</strong> [Date]
          </div>
  
          <h2>1. ANNUAL LEAVE</h2>
          <div class="leave-type">
            <strong>Days per Year:</strong> 20<br>
            <strong>Carry Forward:</strong> Max 5 days<br>
            <strong>Paid Status:</strong> Paid
          </div>
  
          <h2>2. SICK LEAVE</h2>
          <div class="leave-type">
            <strong>Days per Year:</strong> 10<br>
            <strong>Carry Forward:</strong> Not allowed<br>
            <strong>Paid Status:</strong> Paid
          </div>
  
          <h2>3. CASUAL LEAVE</h2>
          <div class="leave-type">
            <strong>Days per Year:</strong> 7<br>
            <strong>Carry Forward:</strong> Not allowed<br>
            <strong>Paid Status:</strong> Paid
          </div>
  
          <h2>4. MATERNITY LEAVE</h2>
          <div class="leave-type">
            <strong>Days per Year:</strong> 180<br>
            <strong>Carry Forward:</strong> Not allowed<br>
            <strong>Paid Status:</strong> Paid
          </div>
  
          <h2>5. PATERNITY LEAVE</h2>
          <div class="leave-type">
            <strong>Days per Year:</strong> 15<br>
            <strong>Carry Forward:</strong> Not allowed<br>
            <strong>Paid Status:</strong> Paid
          </div>
  
          <h2>6. BEREAVEMENT LEAVE</h2>
          <div class="leave-type">
            <strong>Days per Year:</strong> 5<br>
            <strong>Carry Forward:</strong> Not allowed<br>
            <strong>Paid Status:</strong> Paid
          </div>
  
          <h2>GENERAL RULES & GUIDELINES</h2>
          <div class="rules">
            <ul>
              <li>All leave requests must be submitted through HRMS at least 3 working days in advance.</li>
              <li>Public holidays are not counted as leave days.</li>
              <li>Leave applications require manager approval.</li>
              <li>Unused leave cannot be encashed unless specified by company policy.</li>
              <li>Medical certificates required for sick leave exceeding 2 consecutive days.</li>
              <li>Leave balance must be checked before applying for leave.</li>
            </ul>
          </div>
  
          <div class="footer">
            <p><em>This is a template. Please customize according to your organization's policies and requirements.</em></p>
            <p>Generated on: ${dayjs().tz(companyConfig.timezone).format(companyConfig.dateFormat)}</p>
          </div>
        </body>
        </html>
      `;
  
      // Create blob with proper Word MIME type
      const blob = new Blob([htmlContent], {
        type: 'application/msword;charset=utf-8'
      });
  
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Leave_Policy_Template.doc';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
  
      showSnackbar('Template downloaded successfully', 'success');
    } catch (error) {
      //console.error('Download error:', error);
      showSnackbar('Failed to download template. Please try again.', 'error');
    }
  };

  const handleExtractFields = async () => {
    if (!file) {
      showSnackbar('Please select a file first', 'error');
      return;
    }

    setExtracting(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('policyFile', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/policies/extract-fields`,
        formDataToSend,
        {
          headers: {
            
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (res.data.success && res.data.fields) {
        const fields = res.data.fields;
        const updatedFormData = {
          ...formData,
          title: fields.title || formData.title,
          content: fields.content || formData.content,
          general_rules: fields.general_rules || formData.general_rules
        };

        const extractedCustom = {};

        allLeaveTypes.forEach(type => {
          if (fields[type]) {
            try {
              let parsedData = JSON.parse(fields[type]);
              const cf = parsedData.carry_forward || '';
              delete parsedData.carry_forward;
              let cfType = 'none';
              let cfLimit = '';
              if (cf.toLowerCase().includes('all') || cf.toLowerCase().includes('unlimited')) {
                cfType = 'all';
              } else if (cf.match(/max\s*(\d+)/i) || cf.match(/up to\s*(\d+)/i)) {
                const numMatch = cf.match(/(\d+)/);
                if (numMatch) {
                  cfLimit = numMatch[1];
                  cfType = 'limited';
                }
              }
              parsedData.carry_forward_type = cfType;
              parsedData.carry_forward_limit = cfLimit;
              const hasContent = parsedData.days || parsedData.paid_status;
              if (hasContent) {
                updatedFormData[type] = parsedData;
              }
            } catch (e) {
              //console.error(`Error parsing ${type} leave:`, e);
            }
          }
        });

        // Add dynamically extracted custom types from custom_policies if AI provided them
        if (fields.custom_policies) {
          try {
            const customObject = typeof fields.custom_policies === 'string' ? JSON.parse(fields.custom_policies) : fields.custom_policies;
            const loadedCustomTypes = Object.keys(customObject);
            setCustomLeaveTypes(prev => [...new Set([...prev, ...loadedCustomTypes])]);

            loadedCustomTypes.forEach(t => {
              updatedFormData[t] = { ...initialTypeData, ...customObject[t] };
            });
          } catch (e) { }
        }

        setFormData(prev => ({ ...prev, ...updatedFormData }));
        showSnackbar('Fields extracted successfully!', 'success');
        setActiveStep(1);
      }
    } catch (err) {
      //console.error('Extraction error:', err);
      const errorMsg = err.response?.data?.details || 'Failed to extract fields';
      showSnackbar(errorMsg, 'error');
    } finally {
      setExtracting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      effective_date: '',
      created_by: formData.created_by,
      annual: { ...initialTypeData },
      sick: { ...initialTypeData },
      casual: { ...initialTypeData },
      maternity: { ...initialTypeData },
      paternity: { ...initialTypeData },
      bereavement: { ...initialTypeData },
      general_rules: '',
      cycle_start_month: 0
    });
    setCustomLeaveTypes([]);
    setNewLeaveTypeName('');
    setIsEditMode(false);
    setError('');
    setActiveStep(0);
    setInputMode('manual');

    const fileInput = document.getElementById('policy-file-upload');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    if (!formData.title || formData.title.trim().length < 3) {
      setError('Title must be at least 3 characters');
      return false;
    }
    if (!formData.effective_date) {
      setError('Please select an effective date');
      return false;
    }
    if (!formData.created_by || !formData.created_by.trim()) {
      setError('Created by is required');
      return false;
    }

    const selectedDate = new Date(formData.effective_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!isEditMode && selectedDate < today) {
      setError('Effective date cannot be in the past');
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitLoading(true);
    setError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('content', formData.content || '');
      formDataToSend.append('effective_date', formData.effective_date);
      formDataToSend.append('created_by', formData.created_by);
      formDataToSend.append('cycle_start_month', formData.cycle_start_month !== undefined ? formData.cycle_start_month : 0);
      const customPoliciesPayload = {};

      allLeaveTypes.forEach(type => {
        let typeData = { ...formData[type] };
        // Reconstruct carry_forward string
        let cfStr = '';
        if (typeData.carry_forward_type === 'all') {
          cfStr = 'All days';
        } else if (typeData.carry_forward_type === 'limited' && typeData.carry_forward_limit) {
          cfStr = `Max ${typeData.carry_forward_limit} days`;
        }
        // Clean up
        delete typeData.carry_forward_type;
        delete typeData.carry_forward_limit;
        if (cfStr) {
          typeData.carry_forward = cfStr;
        } else if ('carry_forward' in typeData) {
          delete typeData.carry_forward;
        }
        if (!typeData.days && !typeData.paid_status && !cfStr) {
          typeData = null;
        }

        if (baseLeaveTypes.includes(type)) {
          formDataToSend.append(type, JSON.stringify(typeData));
        } else if (typeData) {
          customPoliciesPayload[type] = typeData;
        }
      });

      formDataToSend.append('custom_policies', JSON.stringify(customPoliciesPayload));
      formDataToSend.append('general_rules', formData.general_rules || '');
const token = localStorage.getItem('token');
      if (file) {
        formDataToSend.append('policyFile', file);
      }

      const url = isEditMode
        ? `${API_URL}/api/policies/${policy.id}`
        : `${API_URL}/api/policies/create`;

        await (isEditMode ? axios.put : axios.post)(
          url,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

      showSnackbar(`Policy ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
      setOpenDialog(false);
      resetForm();
      fetchPolicy();
    } catch (err) {
      //console.error('Submit Error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.details || 'Failed to save policy';
      setError(errorMsg);
      showSnackbar(errorMsg, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this policy? This action cannot be undone.')) return;
const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/api/policies/${policy.id}`,  {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      showSnackbar('Policy deleted successfully', 'success');
      setPolicy(null);
      resetForm();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete policy';
      showSnackbar(errorMsg, 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Render Functions
  const renderLeaveTypeSection = (type) => {
    const data = formData[type] || { ...initialTypeData };
    const isCustom = !baseLeaveTypes.includes(type);
    const rawDisplay = isCustom ? (data.displayName || type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')) : type.charAt(0).toUpperCase() + type.slice(1);
    const displayName = rawDisplay.toLowerCase().endsWith('leave') ? rawDisplay : `${rawDisplay} Leave`;

    return (
      <Grid item xs={12} key={type}>
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 2,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
            position: 'relative'
          }}
        >
          {isCustom && (
            <IconButton
              size="small"
              color="error"
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => {
                const confirmDelete = window.confirm(`Remove ${displayName} leave?`);
                if (confirmDelete) {
                  setCustomLeaveTypes(prev => prev.filter(t => t !== type));
                  const newFormData = { ...formData };
                  delete newFormData[type];
                  setFormData(newFormData);
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography sx={{ fontSize: '1.5rem' }}>{leaveTypeIcons[type] || '🌟'}</Typography>
            <Typography variant="h6" fontWeight={600}>{displayName}</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Days per Year"
                value={data.days}
                onChange={(e) => handleTypeFieldChange(type, 'days', e.target.value)}
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
                size="small"
                placeholder="e.g., 20"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Paid Status</InputLabel>
                <Select
                  value={data.paid_status}
                  label="Paid Status"
                  onChange={(e) => handleTypeFieldChange(type, 'paid_status', e.target.value)}
                >
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partially_paid">Partially Paid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip title="Carry forward policy for unused days">
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                  <FormControl sx={{ flex: 1 }} size="small">
                    <InputLabel>Carry Forward</InputLabel>
                    <Select
                      value={data.carry_forward_type}
                      label="Carry Forward"
                      onChange={(e) => handleTypeFieldChange(type, 'carry_forward_type', e.target.value)}
                    >
                      <MenuItem value="none">None</MenuItem>
                      <MenuItem value="all">All days</MenuItem>
                      <MenuItem value="limited">Max days</MenuItem>
                    </Select>
                  </FormControl>
                  {data.carry_forward_type === 'limited' && (
                    <TextField
                      label="Max"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={data.carry_forward_limit}
                      onChange={(e) => handleTypeFieldChange(type, 'carry_forward_limit', e.target.value)}
                      size="small"
                      sx={{ width: 80 }}
                      placeholder="5"
                    />
                  )}
                </Box>
              </Tooltip>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
              Choose how you'd like to create your policy
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    border: 2,
                    borderColor: inputMode === 'manual' ? 'primary.main' : 'grey.200',
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => { setInputMode('manual'); setActiveStep(1); }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: 'primary.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                      }}
                    >
                      <EditIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Manual Entry
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Fill in policy details manually with our structured form
                    </Typography>
                    <Chip
                      label="Simple & Quick"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    border: 2,
                    borderColor: inputMode === 'upload' ? 'secondary.main' : 'grey.200',
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)'
                    }
                  }}
                  onClick={() => setInputMode('upload')}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        bgcolor: 'secondary.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px'
                      }}
                    >
                      <UploadFileIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Upload
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Upload your existing policy
                    </Typography>
                    <Chip
                      label="Fastest way"
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />

                    {inputMode === 'upload' && (
                      <Fade in={true}>
                        <Box sx={{ mt: 3, textAlign: 'left' }}>
                          <Stack spacing={2}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadTemplate();
                              }}
                              fullWidth
                            >
                              Download Template
                            </Button>

                            <Box>
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                id="policy-file-upload"
                              />
                              <Button
                                variant="outlined"
                                component="label"
                                htmlFor="policy-file-upload"
                                fullWidth
                                startIcon={<UploadFileIcon />}
                              >
                                {file ? `📄 ${file.name.substring(0, 25)}${file.name.length > 25 ? '...' : ''}` : 'Choose File'}
                              </Button>
                            </Box>

                            {file && (
                              <Button
                                variant="contained"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExtractFields();
                                }}
                                fullWidth
                                disabled={extracting}
                                startIcon={extracting ? <CircularProgress size={20} /> : null}
                              >
                                {extracting ? 'Extracting...' : 'Extract & Continue'}
                              </Button>
                            )}

                            <Alert severity="info" sx={{ mt: 1 }}>
                              <Typography variant="caption">
                                Supported: PDF, DOC, DOCX (Max 10MB)
                              </Typography>
                            </Alert>
                          </Stack>
                        </Box>
                      </Fade>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
                  <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Policy Title"
                        name="title"
                        value={formData.title}
                        onChange={handleGeneralChange}
                        fullWidth
                        required
                        placeholder="e.g., Company Leave Management Policy 2024"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Effective Date"
                        name="effective_date"
                        type="date"
                        value={formData.effective_date}
                        onChange={handleGeneralChange}
                        fullWidth
                        required
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="cycle-start-month-label">Leave Cycle Start Month</InputLabel>
                        <Select
                          labelId="cycle-start-month-label"
                          label="Leave Cycle Start Month"
                          name="cycle_start_month"
                          value={formData.cycle_start_month !== undefined ? formData.cycle_start_month : 0}
                          onChange={handleGeneralChange}
                        >
                          <MenuItem value={0}>January</MenuItem>
                          <MenuItem value={1}>February</MenuItem>
                          <MenuItem value={2}>March</MenuItem>
                          <MenuItem value={3}>April</MenuItem>
                          <MenuItem value={4}>May</MenuItem>
                          <MenuItem value={5}>June</MenuItem>
                          <MenuItem value={6}>July</MenuItem>
                          <MenuItem value={7}>August</MenuItem>
                          <MenuItem value={8}>September</MenuItem>
                          <MenuItem value={9}>October</MenuItem>
                          <MenuItem value={10}>November</MenuItem>
                          <MenuItem value={11}>December</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Leave Types Configuration" color="primary" />
                </Divider>
              </Grid>

              {allLeaveTypes.map(type => renderLeaveTypeSection(type))}

              <Grid item xs={12} sx={{ mt: 2, mb: 3 }}>
                <Paper sx={{ p: 3, bgcolor: '#f0f4ff', border: '1px dashed', borderColor: 'primary.main' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Add Custom Leave Type
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      label="Custom Leave Name"
                      placeholder="e.g. Compensatory Off"
                      value={newLeaveTypeName}
                      onChange={(e) => setNewLeaveTypeName(e.target.value)}
                      size="small"
                      sx={{ flex: 1, bgcolor: 'background.paper' }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddCustomLeaveType}
                      disabled={!newLeaveTypeName.trim()}
                    >
                      Add Type
                    </Button>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Additional Information" color="primary" />
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                    General Rules & Guidelines
                  </Typography>
                  <TextField
                    name="general_rules"
                    label="General Rules"
                    multiline
                    rows={4}
                    value={formData.general_rules}
                    onChange={handleGeneralChange}
                    fullWidth
                    placeholder="e.g., All leave requests must be submitted through HRMS..."
                    helperText="Common rules that apply to all leave types"
                  />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                    Full Policy Content (Optional)
                  </Typography>
                  <TextField
                    name="content"
                    label="Additional Policy Content"
                    multiline
                    rows={6}
                    value={formData.content}
                    onChange={handleGeneralChange}
                    fullWidth
                    placeholder="Any additional policy details..."
                    helperText="Leave blank to auto-generate from sections above"
                  />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Created By"
                  name="created_by"
                  value={formData.created_by}
                  onChange={handleGeneralChange}
                  fullWidth
                  disabled
                  required
                  size="small"
                  sx={{ bgcolor: 'grey.100' }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  // Helper Functions
  const hasLeaveData = (leaveData) => {
    if (!leaveData) return false;
    return leaveData.days || leaveData.paid_status;
  };

  // Loading State
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 3 }} />
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
      </Container>
    );
  }

  // Main Render
  return (
    <Container maxWidth={false} sx={{ py: 4, width: '100%' }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Leave Policies
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Configure and manage your organization's leave policy
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={policy ? <EditIcon /> : <AddIcon />}
            onClick={() => {
              if (policy) {
                loadPolicyToForm(policy);
                setIsEditMode(true);
              } else {
                resetForm();
                setIsEditMode(false);
              }
              setOpenDialog(true);
            }}
            size="large"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': { bgcolor: 'grey.100' },
              fontWeight: 600,
              px: 4
            }}
          >
            {policy ? 'Edit Policy' : 'Create Policy'}
          </Button>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ mt: 3, bgcolor: 'rgba(255,255,255,0.95)' }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
      </Paper>

      {/* Policy Display Section */}
      {policy ? (
        <Fade in={true}>
          <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {/* Policy Header */}
            <Box
              sx={{
                p: 3,
                bgcolor: 'success.50',
                borderBottom: '1px solid',
                borderColor: 'success.200'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: 'success.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Chip
                      label="ACTIVE POLICY"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 600, mb: 1 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      {policy.title}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={() => {
                      loadPolicyToForm(policy);
                      setIsEditMode(true);
                      setOpenDialog(true);
                    }}
                    sx={{
                      bgcolor: 'white',
                      '&:hover': { bgcolor: 'primary.50' }
                    }}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleDelete}
                    sx={{
                      bgcolor: 'white',
                      '&:hover': { bgcolor: 'error.50', color: 'error.main' }
                    }}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Effective Date:</strong> {dayjs(policy.effective_date).tz(companyConfig.timezone).format(companyConfig.dateFormat)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Leave Cycle:</strong> {getCycleDisplay(policy.cycle_start_month)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Created By:</strong> {policy.created_by}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Policy Content */}
            <Box sx={{ p: 4, maxHeight: '600px', overflowY: 'auto', bgcolor: 'background.paper' }}>
              <Grid container spacing={3}>
                {(() => {
                  const typesToRender = [...baseLeaveTypes];
                  let parsedCustom = {};
                  if (policy.custom_policies) {
                    try {
                      parsedCustom = typeof policy.custom_policies === 'string'
                        ? JSON.parse(policy.custom_policies)
                        : policy.custom_policies;
                      typesToRender.push(...Object.keys(parsedCustom));
                    } catch (e) { }
                  }

                  return typesToRender.map(type => {
                    let leaveData = null;
                    try {
                      if (baseLeaveTypes.includes(type) && policy[type]) {
                        leaveData = JSON.parse(policy[type]);
                      } else if (parsedCustom[type]) {
                        leaveData = typeof parsedCustom[type] === 'string' ? JSON.parse(parsedCustom[type]) : parsedCustom[type];
                      }
                    } catch (e) { 
                      //console.error('Error parsing leave details for read-only map', e);
                       }

                    if (!hasLeaveData(leaveData)) return null;

                    // Only display fields that exist in the form: days, paid_status, carry_forward
                    const displayFields = [];
                    if (leaveData.days) {
                      displayFields.push({ label: 'Days per Year', value: leaveData.days });
                    }
                    if (leaveData.paid_status) {
                      displayFields.push({ label: 'Paid Status', value: leaveData.paid_status });
                    }
                    if (leaveData.carry_forward) {
                      displayFields.push({ label: 'Carry Forward', value: leaveData.carry_forward });
                    }

                    if (displayFields.length === 0) return null;

                    return (
                      <Grid item xs={12} md={6} key={type}>
                        <Paper sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography sx={{ fontSize: '1.3rem' }}>{leaveTypeIcons[type]}</Typography>
                            <Typography variant="h6" fontWeight={600}>
                              {(() => {
                                const display = (leaveData && leaveData.displayName) ||
                                  type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                                return display.toLowerCase().endsWith('leave') ? display : `${display} Leave`;
                              })()}
                            </Typography>
                          </Box>
                          {displayFields.map(({ label, value }) => (
                            <Typography key={label} sx={{ mb: 0.5, fontSize: '0.95rem' }}>
                              <strong>{label}:</strong> {value}
                            </Typography>
                          ))}
                        </Paper>
                      </Grid>
                    );
                  });
                })()}
              </Grid>

              {policy.general_rules && (
                <Box sx={{ mt: 3 }}>
                  <Divider sx={{ mb: 2 }}>
                    <Chip label="General Rules" size="small" />
                  </Divider>
                  <Paper sx={{ p: 3, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {policy.general_rules}
                    </Typography>
                  </Paper>
                </Box>
              )}


            </Box>
          </Paper>
        </Fade>
      ) : (
        <Fade in={true}>
          <Paper
            elevation={2}
            sx={{
              p: 8,
              textAlign: 'center',
              borderRadius: 3,
              border: '2px dashed',
              borderColor: 'grey.300'
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}
            >
              <UploadFileIcon sx={{ fontSize: 40, color: 'grey.400' }} />
            </Box>
            <Typography variant="h5" fontWeight={600} gutterBottom color="text.secondary">
              No Leave Policy Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
              Get started by creating a comprehensive leave management policy
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
                setIsEditMode(false);
              }}
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Create Your First Policy
            </Button>
          </Paper>
        </Fade>
      )}

      {/* Policy Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => { setOpenDialog(false); resetForm(); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" fontWeight={700}>
            {isEditMode ? 'Edit' : 'Create'} Leave Management Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isEditMode ? 'Update your existing policy' : 'Set up a new leave policy'}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {!isEditMode && (
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {renderStepContent()}
        </DialogContent>

        <DialogActions sx={{ px: 4, py: 2.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          {activeStep > 0 && !isEditMode && (
            <Button
              onClick={() => setActiveStep(prev => prev - 1)}
              variant="outlined"
            >
              Back
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={() => { setOpenDialog(false); resetForm(); }}
            disabled={submitLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          {activeStep === 1 && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitLoading || !formData.title || !formData.effective_date}
              startIcon={submitLoading ? <CircularProgress size={20} /> : null}
              sx={{ px: 4, fontWeight: 600 }}
            >
              {submitLoading ? 'Saving...' : (isEditMode ? 'Update Policy' : 'Create Policy')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LeavePolicyManagement;